import type { HomePageCarouselItem } from '@bulknutrients/bulk-shared-lib/interfaces/ICarousel';
import type { LocalStorageProduct } from '@bulknutrients/bulk-shared-lib/interfaces/LocalStorage';
import type { BaseProduct } from '@bulknutrients/bulk-shared-lib/interfaces/Product';
import type { CloudinaryAsset } from '@bulknutrients/bulk-shared-lib/interfaces/Sanity';
import * as schema from '@bulknutrients/bulk-shared-lib/lib/drizzle/forms/schema';
import { drizzle } from '@bulknutrients/bulk-shared-lib/lib/drizzle/instances';
import { blurUrl } from '@bulknutrients/bulk-shared-lib/lib/images';
import {
  getIndexedDBStoreData,
  Stores,
} from '@bulknutrients/bulk-shared-lib/lib/local/db';
import { getReviewCount } from '@bulknutrients/bulk-shared-lib/lib/postgres/forms';
import { cdnClient } from '@bulknutrients/bulk-shared-lib/lib/sanity/client';
import {
  getActiveHomeCIs,
  getDraftHomeCIs,
  getHomePageNewProducts,
  getHomePagePopularProducts,
  getHomePageReviews,
  getHomeVideoCTAs,
} from '@bulknutrients/bulk-shared-lib/lib/sanity/homePage';
import { addBigCommerceDataToBaseProducts } from '@bulknutrients/bulk-shared-lib/lib/sanity/products/transform';
import { removeUndefined } from '@bulknutrients/bulk-shared-lib/utils';
import { Button } from '@components/Button';
import { EmblaCarousel } from '@components/Carousels/Carousel';
import { Container } from '@components/Container';
import { HomePageCTA } from '@components/HomePage/HomePageCTA';
import { HomepageLayout } from '@components/Layouts';
import { CustomImage } from '@components/Media/Image';
import { getImageSrcUrl } from '@components/Media/Image/Image';
import { StarRating } from '@components/Product/StarRating';
import { ProductCardVerticalVariant } from '@components/ProductCard/ProductCard';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { EveryBodyTumbleWord } from '@components/WordTumbler/WordTumbler';
import { useCustomer } from '@hooks/customer/useCustomer';
import { log, logBreadcrumb } from '@lib/logs';
import type { GetStaticProps } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import { Pool } from 'pg';
import { type ReactElement, useEffect, useRef, useState } from 'react';

const headerCategories = [
  { label: 'top sellers', href: '/categories/top-sellers' },
  { label: 'proteins', href: '/categories/proteins' },
  { label: 'pre-workouts', href: '/categories/pre-intra-post-workout/pre' },
  {
    label: 'creatine',
    href: '/categories/shop-by-ingredient/creatine-monohydrate',
  },
  { label: 'capsules', href: '/categories/capsules' },
];

const categories = [
  {
    public_id: 'oqyhu3aow2dwhqsdpsjj',
    altText: 'Shop all',
    label: 'Shop all',
    href: '/categories/all-products',
    _type: 'cloudinary.asset',
  },
  {
    public_id: 'zqv6tdzwmrwdgjqsmevs',
    altText: 'Health & Wellness',
    label: 'Health & Wellness',
    href: '/categories/health-wellness',
    _type: 'cloudinary.asset',
  },
  {
    public_id: 'otr95rr9lsevykmr9vnz',
    altText: 'Strength',
    label: 'Strength',
    href: '/categories/gains-growth',
    _type: 'cloudinary.asset',
  },
  {
    public_id: 'uvhlmkv6my52bvwn37ra',
    altText: 'Weight Loss',
    label: 'Weight Loss',
    href: '/categories/weightloss',
    _type: 'cloudinary.asset',
  },
  {
    public_id: 'csrnmqh0i8gzgc2ezy3b',
    altText: 'Sports Performance',
    label: 'Sports Performance',
    href: '/categories/sports-performance',
    _type: 'cloudinary.asset',
  },
  {
    public_id: 'e4dkddy88sxnbe5cqk0y',
    altText: 'Recovery',
    label: 'Recovery',
    href: '/categories/pre-intra-post-workout/post',
    _type: 'cloudinary.asset',
  },
  {
    public_id: 'ynsa9eie0ygfybaiojj7',
    altText: 'Gut Health',
    label: 'Gut Health',
    href: '/categories/gut-health',
    _type: 'cloudinary.asset',
  },
  {
    public_id: 'lrofwv0q9qejqafrdsb7',
    altText: 'Plant Based',
    label: 'Plant Based',
    href: '/categories/plant-based',
    _type: 'cloudinary.asset',
  },
  {
    public_id: 'rapjnhnelfikscleua5o',
    altText: 'Samples',
    label: 'Samples',
    href: '/categories/proteins/sample-packs',
    _type: 'cloudinary.asset',
  },
];

const badges = [
  {
    public_id: 't9endhdmtzywywoin2sq',
    altText: 'Pure. Tested. Proven. badge',
    title: 'Pure. Tested. Proven.',
    description: 'Over 700 lab tests published on our website.',
  },
  {
    public_id: 'rsgxwue1dwpxouauqv5t',
    altText: 'Aussie owned & operated Badge',
    title: 'Aussie owned & operated',
    description:
      'Bulk Nutrients is a proudly Australian owned and operated business since 2008.',
  },
  {
    public_id: 'nrqrxjgdwctnljoht5kh',
    altText: 'HASTA certified badge',
    title: 'Hasta certified',
    description: 'Bulk Nutrients supplies HASTA Certified supplements.',
  },
  {
    public_id: 'ncwnnh5oi4ke2spjep0w',
    altText: 'Preferred by athletes badge',
    title: 'Preferred by athletes',
    description:
      'Trusted by Tasmania Jack Jumpers, North Melbourne Football Club and other elite sporting clubs.',
  },
];

export const ReviewCard = ({ review }) => (
  <div className="bg-white flex-1 dark:bg-grey-light flex flex-col justify-center relative pt-10 rounded-2xl w-[275px] overflow-visible mt-12 transition-all duration-300 hover:scale-103">
    <div className="absolute -top-12 left-[32%]">
      <CustomImage
        round={true}
        image={review.image}
        width={100}
        height={100}
        templateOverride={{ crop: 'auto' }}
      />
    </div>
    <div className="p-4 flex-1 flex flex-col justify-between">
      <Paragraph className="dark:text-black">"{review.body}"</Paragraph>
      {review.isAmbassador && (
        <div className="uppercase font-bold text-center text-black">
          Bulk Ambassador
        </div>
      )}
      <div className="uppercase font-bold text-center text-black">
        {review.displayName}
      </div>
    </div>
    <Button
      href={review.product.slug}
      buttonStyle="secondary"
      className="w-full rounded-t-none rounded-b-2xl"
    >
      {review.product.name}
    </Button>
  </div>
);

const CategoryNavBar = () => {
  const [recentlyViewed, setRecentlyViewed] = useState(headerCategories);

  useEffect(() => {
    const getRecent = async () => {
      const current = await getIndexedDBStoreData<LocalStorageProduct[]>(
        Stores.keyvaluepairs,
        'recentlyViewed'
      );

      const ids = await cdnClient.fetch(
        `*[_type == "product" && discontinued != true].productId`
      );

      const products = current
        .filter((c) => ids.includes(c.product.entityId))
        .slice(0, 5)
        .map(({ product }) => ({
          label: product.shortName ?? product.name,
          href: product.slug,
        }));

      setRecentlyViewed([...products, ...headerCategories].slice(0, 5));
    };

    getRecent();
  }, []);

  return (
    <div className="p-3 overflow-x-auto">
      <div className="flex flex-row gap-4 justify-evenly max-w-max-width min-w-max mx-auto">
        {recentlyViewed.map((c) => (
          <TextLink
            href={c.href}
            key={c.label}
            className="uppercase text-black dark:text-white hover:dark:text-white hover:text-black lg:text-lg !font-extrabold no-underline 
                    hover:underline active:underline active:dark:text-white active:text-black whitespace-nowrap"
          >
            {c.label}
          </TextLink>
        ))}
      </div>
    </div>
  );
};

function Badges() {
  const [inView, setInView] = useState(false);
  const [visibleIndex, setVisibleIndex] = useState(0);

  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // used to animate info badges when viewed
    if (!ref.current) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    });

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // rotate info badges on mobile every 4 seconds
    const interval = setInterval(() => {
      setVisibleIndex((prev) => (prev + 1) % badges.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="relative flex flex-row mb-6 justify-center h-[100px] lg:h-auto"
      ref={ref}
    >
      {badges.map((b, i) => (
        <div
          style={{ '--delay': `${i * 0.5}s` } as React.CSSProperties}
          key={b.title}
          data-open={i === visibleIndex}
          className={`
                  flex flex-row items-start gap-2 px-2
                  ${i === visibleIndex ? 'absolute lg:relative opacity-100 animate-[fadeInFromRight_1.5s_forwards]' : 'absolute lg:relative opacity-0'}
                  lg:flex-1 lg:border-r lg:last:border-r-0 lg:dark:border-white lg:max-w-[400px] lg:opacity-0
                  ${inView ? 'lg-delay lg:animate-[fade-in_1.5s_forwards]' : ''}
                `}
        >
          <div className="w-[100px] h-[100px] shrink-0">
            <CustomImage
              image={
                {
                  asset: {
                    public_id: b.public_id,
                  },
                  _type: 'cloudinary.asset',
                  altText: b.altText,
                } as CloudinaryAsset
              }
              width={150}
              height={150}
              className="dark:invert"
            />
          </div>
          <div className="flex flex-col min-w-0">
            <div className="uppercase text-black dark:text-white text-xl font-extrabold font-secondary">
              {b.title}
            </div>
            {b.description}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Page({
  reviewCount,
  reviews,
  homeCarouselItems,
  homeVideoCTA,
  popularProducts,
  newProducts,
}: {
  reviewCount: number;
  reviews: Awaited<ReturnType<typeof getHomePageReviews>>;
  homeCarouselItems: HomePageCarouselItem[];
  homeVideoCTA: Awaited<ReturnType<typeof getHomeVideoCTAs>>;
  popularProducts: BaseProduct[];
  newProducts: BaseProduct[];
}): ReactElement {
  const { customer } = useCustomer();

  return (
    <>
      <Head>
        <title>Bulk Nutrients | Australia's first choice for supplements</title>
        <meta
          name="title"
          content="Bulk Nutrients | Australia's first choice for supplements"
        />
        <meta
          name="description"
          content="Bulk Nutrients is Australia's #1 online store for protein powders, supplements and vitamins. Explore our range today and transform your fitness journey!"
        />
        <link rel="canonical" href={`${process.env.NEXT_PUBLIC_SERVER_URL}`} />
      </Head>
      <main id="main-content">
        <Container as="article" className="cursor-default">
          {/* HEADER CATEGORY NAV BAR */}
          <div className="full-bleed dark:bg-grey-15">
            <CategoryNavBar />
          </div>

          {/* CTA */}
          <div className="flex flex-col full-bleed">
            <HomePageCTA
              carouselItems={homeCarouselItems}
              homeVideoCTA={homeVideoCTA}
            />
          </div>

          <h1>
            Bulk Nutrients is Australia's first choice for protein powders,
            nutritional supplements and vitamins
          </h1>

          {/* POPULAR PRODUCTS CAROUSEL */}
          <div className="full-bleed my-6 pl-6">
            <EmblaCarousel
              title="Popular Products"
              titleClass="!font-extrabold"
              slides={popularProducts.map((item) => {
                return (
                  <ProductCardVerticalVariant
                    product={item}
                    key={item.entityId}
                    location="Popular Product Homepage"
                  />
                );
              })}
            />
          </div>

          {/* ANIMATED INFO BADGES */}
          <Badges />

          {/* NEW PRODUCTS CAROUSEL */}
          <div className="full-bleed my-6 pl-6">
            <EmblaCarousel
              title="New Products"
              titleClass="!font-extrabold"
              slides={newProducts.map((item) => {
                return (
                  <ProductCardVerticalVariant
                    product={item}
                    key={item.entityId}
                    location="New Product Homepage"
                  />
                );
              })}
            />
          </div>

          {/* SHOP BY CATEGORY CAROUSEL */}
          <div className="py-6 full-bleed pl-6 bg-grey-light dark:bg-black">
            <EmblaCarousel
              title="Shop by category"
              titleClass="!font-extrabold"
              slides={categories.map((c) => {
                return (
                  <div
                    className="relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:scale-103"
                    key={c.label}
                  >
                    <CustomImage
                      image={
                        {
                          asset: { public_id: c.public_id },
                          altText: c.altText,
                          _type: 'cloudinary.asset',
                          link: c.href,
                        } as CloudinaryAsset
                      }
                      width={275}
                      templateOverride={removeUndefined({
                        // crop: 'auto', took this off as was only affecting samples and unideally
                        width: 275,
                      })}
                    />
                    <div className="absolute inset-0 flex-col flex justify-center text-4xl text-white uppercase font-extrabold items-center text-center pointer-events-none">
                      {c.label}
                    </div>
                  </div>
                );
              })}
            />
          </div>

          {/* CUSTOMER/AMBASSADOR REVIEW CAROUSEL */}
          <div className="full-bleed py-6 pl-6 bg-grey-light dark:bg-grey-darkest flex flex-col gap-4">
            <StarRating rating={5} size={30} className="gap-2 self-center" />
            <div className="font-extrabold uppercase text-center text-orange text-3xl pr-6">
              <span className="text-black dark:text-white">
                over {reviewCount.toLocaleString()}
              </span>{' '}
              5 star ratings & reviews
            </div>
            <EmblaCarousel
              slides={reviews.map((r) => {
                return (
                  <ReviewCard key={`review_pos${r.position}`} review={r} />
                );
              })}
            />
          </div>

          {/* BN INFO GRID */}
          <div className="grid grid-cols-1 sm:grid-cols-10 max-w-max-width gap-6 justify-self-center items-center py-6">
            <div className="sm:col-span-6">
              <div className="uppercase text-black dark:text-white text-xl font-extrabold">
                <EveryBodyTumbleWord
                  leadSentence="SUPPLEMENTS FOR EVERY"
                  inline={true}
                  tumbleWords={[
                    'body',
                    'lift',
                    'adventure',
                    'step',
                    'recovery',
                  ]}
                />
              </div>
              <Paragraph>
                At Bulk Nutrients, we're here to fuel your health and fitness
                journey with clean, effective supplements. Whether you're
                challenging your limits, starting fresh, or prioritising your
                everyday wellbeing, we offer a wide range of supplements to
                support your goals.
              </Paragraph>
              <Paragraph>
                You'll find everything from Protein Powders and Amino Acids to
                Pre Workout, Intra Workout and Post Workout Supplements,
                Capsules, Carbohydrate Supplements, and more - all crafted to
                help you train harder, recover faster, and feel better every
                day.
              </Paragraph>
              <Button buttonStyle="primary" href="/about-us" className="w-fit">
                Learn more
              </Button>
            </div>
            <div className="sm:col-span-4 self-start">
              <Image
                src={getImageSrcUrl({
                  image: {
                    // @ts-expect-error
                    asset: { public_id: 'oqwewbbdwr6pjya8nalw' },
                    _type: 'cloudinary.asset',
                    altText: "Bulk Nutrients' ambassador with products",
                  },
                  width: 600,
                  height: undefined,
                  templateOverride: {
                    crop: 'auto',
                    gravity: 'auto',
                    aspectRatio: '5:3',
                  },
                })}
                alt="Bulk Nutrients' ambassador with products"
                width={600}
                height={600}
                className="mx-auto my-0 h-full max-w-full object-cover rounded-2xl" // only way object-cover doesn't work on custom image bc direct parent div has w-fit/h-fit
                priority={true}
                unoptimized
                placeholder="blur"
                blurDataURL={blurUrl(600, 600)}
              />
            </div>
            <div className="hidden sm:flex sm:order-0 sm:col-span-4 self-start">
              <Image
                src={getImageSrcUrl({
                  image: {
                    // @ts-expect-error
                    asset: { public_id: 'bjnxxjo5qugs79fyhal1' },
                    _type: 'cloudinary.asset',
                    altText: "Bulk Nutrients' sample packs",
                  },
                  width: 600,
                  height: undefined,
                  templateOverride: {
                    crop: 'auto',
                    gravity: 'auto',
                    aspectRatio: '5:3',
                  },
                })}
                alt="Bulk Nutrients' sample packs"
                width={600}
                height={600}
                className="mx-auto my-0 h-full max-w-full object-cover rounded-2xl" // only way object-cover doesn't work on custom image bc direct parent div has w-fit/h-fit
                priority={true}
                unoptimized
                placeholder="blur"
                blurDataURL={blurUrl(600, 250)}
              />
            </div>
            <div className="sm:col-span-6">
              <div className="uppercase text-black dark:text-white text-xl font-extrabold">
                Join our rewards program
                <br /> and get more from every order
              </div>
              <Paragraph>
                We reckon loyalty should come with more than just good gains.
                Join our free Rewards Program and earn rewards points every time
                you shop. The more you buy, the more perks you unlock including
                free shipping, exclusive discounts, and bonus offers just for
                members.
              </Paragraph>
              <Paragraph>
                It's our way of saying thanks for choosing Bulk. Whether you've
                been with us for years or you're just getting started, we're
                stoked to have you on the journey.
              </Paragraph>
              <Paragraph className="font-bold">
                Create your account and start earning from your very first
                order.
              </Paragraph>
              <Button
                buttonStyle="primary"
                href={customer ? '/account/rewards' : '/rewards'}
                className="w-fit"
              >
                Find out more
              </Button>
            </div>
          </div>
        </Container>
      </main>
    </>
  );
}

export const getStaticProps: GetStaticProps = async ({
  params,
  preview,
  previewData,
}) => {
  try {
    if (
      !process.env.PGUSER ||
      !process.env.PGPASSWORD_FORMS ||
      !process.env.PGDATABASE_FORMS ||
      !process.env.PGHOST_FORMS
    ) {
      throw new Error('Database environment variables are not set');
    }
    const pool = new Pool({
      user: process.env.PGUSER,
      password: process.env.PGPASSWORD_FORMS,
      database: process.env.PGDATABASE_FORMS,
      host: process.env.PGHOST_FORMS,
      port: 5432,
    });
    const db = drizzle(pool, { schema });

    const { count } = await getReviewCount({
      db,
      stars: '5',
    });

    const reviews = await getHomePageReviews();

    /* Fetch the banner/hero CTA documents from Sanity */
    const homeCarouselItems: HomePageCarouselItem[] = preview
      ? await getDraftHomeCIs()
      : await getActiveHomeCIs();

    const homeVideoCTA = await getHomeVideoCTAs();

    // Fetch the carousel items for the popular products and new products carousels from Sanity
    const popularProducts_base = await getHomePagePopularProducts();
    const newProducts_base = await getHomePageNewProducts();

    const popularProducts =
      await addBigCommerceDataToBaseProducts(popularProducts_base);
    const newProducts =
      await addBigCommerceDataToBaseProducts(newProducts_base);

    return {
      props: {
        reviewCount: count,
        reviews,
        homeCarouselItems,
        homeVideoCTA,
        popularProducts,
        newProducts,
      },
      // revalidate every hour for homepage carousel items
      revalidate: 3600,
    };
  } catch (error) {
    logBreadcrumb({
      category: 'pageRenderError',
      message: 'Error in getStaticProps for home page',
      level: 'error',
    });
    log({ error });
    throw error;
  }
};

Page.getLayout = function getLayout(page: ReactElement & { props }) {
  return <HomepageLayout>{page}</HomepageLayout>;
};
